import React, {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {useAuth} from '../../../modules/auth'
import {branchesOf} from '../../common/actingBranch'
import {
  CreateJazariUser,
  JazariUser,
  createJazariUser,
  getJazariUsers,
  setJazariUserBranch,
  setJazariUserPhone,
  setJazariUserStatus,
} from './_requests'

/**
 * Mart v2: "Jazari users" — the pickers who work the supplier's shop orders on the Jazari handheld.
 * The admin adds one (name, email, phone, branch; the role is always Picking and the username is
 * made from the name), changes their phone, moves them to another branch, and switches them on or off. Sign-in is Google on the email or an SMS code
 * on the phone, so there is no password.
 */
const JazariUsersList: FC = () => {
  const intl = useIntl()
  const t = (id: string, values?: Record<string, any>) => intl.formatMessage({id}, values)
  const {currentUser} = useAuth()
  const branches = branchesOf(currentUser)
  const showBranch = branches.length > 1

  const [users, setUsers] = useState<JazariUser[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [notice, setNotice] = useState<{type: 'success' | 'danger'; text: string} | null>(null)
  // The row whose phone is being edited, and the number typed so far.
  const [phoneEdit, setPhoneEdit] = useState<{id: number; value: string; error: string} | null>(null)

  const fetchUsers = async () => {
    setLoading(true)
    try {
      setUsers(await getJazariUsers(currentUser?.token, intl.locale))
    } catch (error: any) {
      setUsers([])
      setNotice({type: 'danger', text: error?.response?.data?.message || t('JAZARI_USERS.FETCH_ERROR')})
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currentUser?.token) {
      fetchUsers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.token])

  const toggle = async (user: JazariUser) => {
    setBusyId(user.user_id)
    setNotice(null)
    try {
      const res = await setJazariUserStatus(user.user_id, !user.status, currentUser?.token, intl.locale)
      setUsers((prev) => prev.map((u) => (u.user_id === user.user_id ? res.data ?? {...u, status: !u.status} : u)))
      setNotice({type: 'success', text: res.message})
    } catch (error: any) {
      setNotice({type: 'danger', text: error?.response?.data?.message || t('JAZARI_USERS.SAVE_ERROR')})
    } finally {
      setBusyId(null)
    }
  }

  const savePhone = async (user: JazariUser) => {
    if (!phoneEdit) {
      return
    }
    setBusyId(user.user_id)
    setNotice(null)
    try {
      const res = await setJazariUserPhone(user.user_id, phoneEdit.value.trim(), currentUser?.token, intl.locale)
      setUsers((prev) => prev.map((u) => (u.user_id === user.user_id ? res.data ?? u : u)))
      setPhoneEdit(null)
      setNotice({type: 'success', text: res.message})
    } catch (error: any) {
      const data = error?.response?.data
      setPhoneEdit((prev) => prev && {...prev, error: data?.fields?.phone || data?.message || t('JAZARI_USERS.SAVE_ERROR')})
    } finally {
      setBusyId(null)
    }
  }

  // Refused while the picker is still picking a box; the row then stays on its branch.
  const move = async (user: JazariUser, fulfillmentCenterId: number) => {
    if (fulfillmentCenterId === user.fulfillment_center_id) {
      return
    }
    setBusyId(user.user_id)
    setNotice(null)
    try {
      const res = await setJazariUserBranch(user.user_id, fulfillmentCenterId, currentUser?.token, intl.locale)
      setUsers((prev) => prev.map((u) => (u.user_id === user.user_id ? res.data ?? u : u)))
      setNotice({type: 'success', text: res.message})
    } catch (error: any) {
      setNotice({type: 'danger', text: error?.response?.data?.message || t('JAZARI_USERS.SAVE_ERROR')})
    } finally {
      setBusyId(null)
    }
  }

  return (
    <KTCard>
      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column'>
          <h3 className='fw-bolder m-0'>{t('JAZARI_USERS.TITLE')}</h3>
          <span className='text-muted fs-7 mt-1'>{t('JAZARI_USERS.HINT')}</span>
        </div>
        <div className='card-toolbar'>
          <button type='button' className='btn btn-primary' onClick={() => setShowAdd(true)}>
            <i className='bi bi-plus fs-2'></i>
            {t('JAZARI_USERS.ADD')}
          </button>
        </div>
      </div>
      <KTCardBody className='py-4'>
        {notice && (
          <div className={`alert alert-${notice.type} d-flex justify-content-between align-items-center`} role='alert'>
            <span>{notice.text}</span>
            <button type='button' className='btn-close' onClick={() => setNotice(null)}></button>
          </div>
        )}
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-5'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                <th>{t('JAZARI_USERS.NAME')}</th>
                <th>{t('JAZARI_USERS.USERNAME')}</th>
                <th>{t('USERS.EMAIL')}</th>
                <th>{t('JAZARI_USERS.PHONE')}</th>
                {showBranch && <th>{t('JAZARI_USERS.BRANCH')}</th>}
                <th>{t('JAZARI_USERS.ROLE')}</th>
                <th className='text-end'>{t('USERS.STATUS')}</th>
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-bold'>
              {loading ? (
                <tr>
                  <td colSpan={showBranch ? 7 : 6} className='text-center py-10'>
                    <span className='spinner-border spinner-border-sm'></span>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={showBranch ? 7 : 6} className='text-center text-muted py-10'>
                    {t('JAZARI_USERS.EMPTY')}
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id}>
                    <td className='text-gray-800'>
                      <bdi>
                        {user.firstname} {user.lastname}
                      </bdi>
                    </td>
                    <td>
                      <bdi>{user.username}</bdi>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      {phoneEdit?.id === user.user_id ? (
                        <div>
                          <div className='d-flex align-items-center gap-2'>
                            <input
                              type='tel'
                              dir='ltr'
                              className={`form-control form-control-sm w-150px ${phoneEdit.error ? 'is-invalid' : ''}`}
                              value={phoneEdit.value}
                              autoFocus
                              onChange={(e) => setPhoneEdit({...phoneEdit, value: e.target.value, error: ''})}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') savePhone(user)
                                if (e.key === 'Escape') setPhoneEdit(null)
                              }}
                              aria-label={t('JAZARI_USERS.PHONE')}
                            />
                            <button
                              type='button'
                              className='btn btn-sm btn-primary'
                              disabled={busyId === user.user_id}
                              onClick={() => savePhone(user)}
                            >
                              {t('COMMON.SAVE')}
                            </button>
                            <button type='button' className='btn btn-sm btn-light' onClick={() => setPhoneEdit(null)}>
                              {t('COMMON.CANCEL')}
                            </button>
                          </div>
                          {phoneEdit.error && <div className='invalid-feedback d-block'>{phoneEdit.error}</div>}
                        </div>
                      ) : (
                        <div className='d-flex align-items-center gap-2'>
                          <bdi dir='ltr'>{user.phone}</bdi>
                          <button
                            type='button'
                            className='btn btn-icon btn-sm btn-active-light-primary w-25px h-25px'
                            onClick={() => setPhoneEdit({id: user.user_id, value: user.phone, error: ''})}
                            title={t('JAZARI_USERS.EDIT_PHONE')}
                            aria-label={t('JAZARI_USERS.EDIT_PHONE')}
                          >
                            <i className='bi bi-pencil fs-6'></i>
                          </button>
                        </div>
                      )}
                    </td>
                    {showBranch && (
                      <td>
                        <select
                          className='form-select form-select-sm form-select-solid w-auto'
                          value={user.fulfillment_center_id}
                          disabled={busyId === user.user_id}
                          onChange={(e) => move(user, Number(e.target.value))}
                          aria-label={t('JAZARI_USERS.BRANCH')}
                        >
                          {branches.map((b) => (
                            <option key={b.fulfillment_center_id} value={b.fulfillment_center_id}>
                              {b.branch_name || b.name}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td>{t('JAZARI_USERS.ROLE_PICKING')}</td>
                    <td className='text-end'>
                      <div className='form-check form-switch form-check-custom form-check-solid justify-content-end'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={user.status}
                          disabled={busyId === user.user_id}
                          onChange={() => toggle(user)}
                          aria-label={t('USERS.STATUS')}
                        />
                        <span className={`ms-3 badge ${user.status ? 'badge-light-success' : 'badge-light-danger'}`}>
                          {t(user.status ? 'USERS.ACTIVE' : 'USERS.INACTIVE')}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </KTCardBody>
      {showAdd && (
        <AddJazariUserModal
          onClose={() => setShowAdd(false)}
          onCreated={(message) => {
            setShowAdd(false)
            setNotice({type: 'success', text: message})
            fetchUsers()
          }}
        />
      )}
    </KTCard>
  )
}

const AddJazariUserModal: FC<{onClose: () => void; onCreated: (message: string) => void}> = ({onClose, onCreated}) => {
  const intl = useIntl()
  const t = (id: string) => intl.formatMessage({id})
  const {currentUser} = useAuth()
  const branches = branchesOf(currentUser)
  const [form, setForm] = useState<CreateJazariUser>({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    // One shop: no branch to choose, the server takes it.
    fulfillment_center_id: branches.length === 1 ? branches[0].fulfillment_center_id : null,
  })
  const [fields, setFields] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const set = (key: keyof CreateJazariUser, value: any) => {
    setForm((prev) => ({...prev, [key]: value}))
    setFields((prev) => ({...prev, [key]: ''}))
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    setFields({})
    try {
      const res = await createJazariUser(form, currentUser?.token, intl.locale)
      onCreated(res.message)
    } catch (err: any) {
      const data = err?.response?.data
      setFields(data?.fields || {})
      setError(data?.message || t('JAZARI_USERS.SAVE_ERROR'))
    } finally {
      setSaving(false)
    }
  }

  const input = (key: 'firstname' | 'lastname' | 'email' | 'phone', label: string, type = 'text') => (
    <div className='col-md-6 mb-4'>
      <label className='form-label required'>{t(label)}</label>
      <input
        type={type}
        dir={type === 'tel' || type === 'email' ? 'ltr' : 'auto'}
        className={`form-control ${fields[key] ? 'is-invalid' : ''}`}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        required
      />
      {fields[key] && <div className='invalid-feedback d-block'>{fields[key]}</div>}
    </div>
  )

  return (
    <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050}}>
      <div className='modal-dialog modal-lg modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>{t('JAZARI_USERS.ADD')}</h5>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>
          <form onSubmit={submit}>
            <div className='modal-body'>
              {error && <div className='alert alert-danger mb-4'>{error}</div>}
              <div className='row'>
                {input('firstname', 'JAZARI_USERS.FIRST_NAME')}
                {input('lastname', 'JAZARI_USERS.LAST_NAME')}
                {input('email', 'USERS.EMAIL', 'email')}
                {input('phone', 'JAZARI_USERS.PHONE', 'tel')}
                <div className='col-md-6 mb-4'>
                  <label className='form-label'>{t('JAZARI_USERS.ROLE')}</label>
                  <input type='text' className='form-control' value={t('JAZARI_USERS.ROLE_PICKING')} disabled />
                </div>
                {branches.length > 1 && (
                  <div className='col-md-6 mb-4'>
                    <label className='form-label required'>{t('JAZARI_USERS.BRANCH')}</label>
                    <select
                      className={`form-select ${fields.fulfillment_center_id ? 'is-invalid' : ''}`}
                      value={form.fulfillment_center_id ?? ''}
                      onChange={(e) => set('fulfillment_center_id', e.target.value ? Number(e.target.value) : null)}
                      required
                    >
                      <option value=''>{t('JAZARI_USERS.CHOOSE_BRANCH')}</option>
                      {branches.map((b) => (
                        <option key={b.fulfillment_center_id} value={b.fulfillment_center_id}>
                          {b.branch_name || b.name}
                        </option>
                      ))}
                    </select>
                    {fields.fulfillment_center_id && (
                      <div className='invalid-feedback d-block'>{fields.fulfillment_center_id}</div>
                    )}
                  </div>
                )}
              </div>
              <div className='text-muted fs-7'>{t('JAZARI_USERS.SIGN_IN_HINT')}</div>
            </div>
            <div className='modal-footer d-flex justify-content-end gap-2'>
              <button type='button' className='btn btn-secondary' onClick={onClose}>
                {t('COMMON.CANCEL')}
              </button>
              <button type='submit' className='btn btn-primary' disabled={saving}>
                {saving ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2'></span>
                    {t('COMMON.SAVING')}
                  </>
                ) : (
                  t('COMMON.SAVE')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export {JazariUsersList}
